import { env } from "../../env";
import { Box, FormHelperText, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback": () => void;
          "error-callback": () => void;
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
    __podiumTurnstileScriptLoading?: Promise<void>;
  }
}

interface TurnstileProps {
  error?: string;
  onTokenChange: (token: string) => void;
}

const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve();
  }

  if (window.__podiumTurnstileScriptLoading) {
    return window.__podiumTurnstileScriptLoading;
  }

  window.__podiumTurnstileScriptLoading = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SCRIPT_SRC}"]`
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });

  return window.__podiumTurnstileScriptLoading;
}

function Turnstile({ error, onTokenChange }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const previousErrorRef = useRef<string | undefined>();
  const [scriptError, setScriptError] = useState(false);
  const siteKey = env.REACT_APP_TURNSTILE_SITE_KEY;
  const theme = useTheme();

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useEffect(() => {
    if (!error || previousErrorRef.current === error) {
      previousErrorRef.current = error;
      return;
    }

    previousErrorRef.current = error;
    onTokenChangeRef.current("");
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [error]);

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      return undefined;
    }

    let isMounted = true;

    loadTurnstileScript()
      .then(() => {
        if (!isMounted || !window.turnstile || !containerRef.current) {
          return;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onTokenChangeRef.current(token),
          "expired-callback": () => onTokenChangeRef.current(""),
          "error-callback": () => onTokenChangeRef.current(""),
        });
      })
      .catch(() => {
        if (isMounted) {
          setScriptError(true);
          onTokenChangeRef.current("");
        }
      });

    return () => {
      isMounted = false;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  if (!siteKey) {
    return null;
  }

  return (
    <Box sx={{ my: 2 }}>
      <Box
        sx={{
          alignItems: "center",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          display: "flex",
          justifyContent: "center",
          minHeight: 86,
          px: 2,
          py: 1.5,
          width: "100%",
        }}
      >
        <div ref={containerRef} />
      </Box>
      {(error || scriptError) && (
        <FormHelperText error>
          {error || "Captcha could not be loaded. Please try again."}
        </FormHelperText>
      )}
    </Box>
  );
}

export default Turnstile;
